import React, { useState, useEffect } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../../config/firebaseConfig";
import TutorList from "./TutorList";
import TutorCreationModal from "./TutorCreationModal";
import "../../../styles/common.css";
import "./AITutorPage.css";
import { getPerformance, trace } from "firebase/performance";

const AITutorPage = ({ setPageTitle }) => {
  const [tutors, setTutors] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentTutor, setCurrentTutor] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    setPageTitle("AI Tutors");
    fetchTutors();
  }, [setPageTitle]);

  const fetchTutors = async () => {
    const fetchTutorsTrace = trace(getPerformance(), "fetchTutors");
    fetchTutorsTrace.start();
    setIsLoading(true);
    try {
      const getTutorsFunction = httpsCallable(functions, "getTutors");
      const result = await getTutorsFunction();

      if (result.data && result.data.tutors) {
        setTutors(result.data.tutors);
      } else {
        setTutors([]);
      }
    } catch (error) {
      console.error("Error fetching tutors:", error);
      setTutors([]);
    } finally {
      setIsLoading(false);
      fetchTutorsTrace.stop();
    }
  };

  const openCreateModal = () => {
    setCurrentTutor(null);
    setIsEditing(false);
    setIsModalOpen(true);
  };

  const openEditModal = (tutor) => {
    setCurrentTutor(tutor);
    setIsEditing(true);
    setIsModalOpen(true);
  };

  const handleUpdateTutor = async (tutorData, tutorId) => {
    if (!tutorData) {
      setIsModalOpen(false);
      return;
    }

    try {
      let result;
      if (tutorId) {
        const updateTutorFunction = httpsCallable(functions, "updateTutor");
        result = await updateTutorFunction({
          tutorId,
          tutorData,
        });
      } else {
        const createTutorFunction = httpsCallable(functions, "createTutor");
        result = await createTutorFunction({
          tutorData,
        });
      }

      if (result.data) {
        fetchTutors();
      }
    } catch (error) {
      console.error("Error updating tutor:", error);
      alert("Failed to update tutor. Please try again.");
    } finally {
      setIsModalOpen(false);
    }
  };

  const handleDeleteTutor = async (tutorId) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this tutor?"
    );
    if (confirmDelete) {
      try {
        const deleteTutorFunction = httpsCallable(functions, "deleteTutor");
        await deleteTutorFunction({ tutorId });
        fetchTutors();
      } catch (error) {
        console.error("Error deleting tutor:", error);
        alert("Failed to delete tutor. Please try again.");
      }
    }
  };

  return (
    <div className="container ai-tutor-page-container">
      <div className="ai-tutor-top-buttons">
        <button className="ai-tutor-create-button" onClick={openCreateModal}>
          Create New Tutor
        </button>
      </div>

      <div className="ai-tutor-management-section">
        {isLoading ? (
          <div className="ai-tutor-loading-container">
            <div className="ai-tutor-loading-ring"></div>
            <p>Loading tutors...</p>
          </div>
        ) : (
          <TutorList
            tutors={tutors}
            onEdit={openEditModal}
            onDelete={handleDeleteTutor}
          />
        )}
      </div>

      {isModalOpen && (
        <TutorCreationModal
          onClose={() => setIsModalOpen(false)}
          onSave={handleUpdateTutor}
          tutor={currentTutor}
          isEditing={isEditing}
        />
      )}
    </div>
  );
};

export default AITutorPage;
